import React from "react";
import styled from "styled-components";
import Xarrow, { Xwrapper } from "react-xarrows";
import { XMappingDestination } from "./Components/XMappingDestination";
import { XMappingRules } from "./Components/XMappingRules";
import { XMappingSource } from "./Components/XMappingSource";
import { MappingEditContext } from "./Builder/MappingEditContext";
import { MappingConfiguration } from "../rule-engine-core/Mapping/MappingConfiguration";
import { MappingConstants } from "../rule-engine-core/Mapping/MappingConstants";
import { Sidebar, SidebarItem } from "../CommonUI/BuilderUI/Sidebar";
import { MappingBuilderPage } from "./Builder/MappingBuilderPage";

import { XExecutableTester } from "../CommonUI/Testing/XExecutableTester";

import { TestDataIcon } from "../CommonUI/Icon/TestDataIcon";
import { Topbar } from "../CommonUI/BuilderUI/Topbar";
import { ObserveState } from "../CommonUI/StateManagement/ObserveState";
import { DataInputIcon } from "../CommonUI/Icon/DataInputIcon";
import { DataOutputIcon } from "../CommonUI/Icon/DataOutputIcon";
import { MappingIcon } from "../CommonUI/Icon/MappingIcon";
import { AutoBind } from "../rule-engine-core/Common/AutoBind";
import { StateManager } from "../rule-engine-core/Common/StateManager";
import { DataBuilder } from "../DataBuilder/DataBuilder";
import { UnitTestConfiguration } from "../rule-engine-core/Common/UnitTesting/UnitTestConfiguration";
import { ReactRenderHook } from "../CommonUI/ReactRenderHook";
import { ObserveMultiState } from "../CommonUI/StateManagement/ObserveMultiState";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { UIStyleConstants } from "../rule-engine-core/Common/UIStyleConstants";

export interface IMappingBuilder {
    definition: MappingConfiguration;
    mappingBuilderName?: string;
    onMappingBuilderNameUpdate?: (newName: string) => void;
    onSave?: (data: MappingConfiguration) => void;
    onCancel?: () => void;
    saveUnitTest?: (unitTest: UnitTestConfiguration) => void;
    getUnitTests?: () => Promise<UnitTestConfiguration[]>;
}

export class MappingBuilder extends React.Component<IMappingBuilder, {}> {
    editContext: MappingEditContext;
    tabs: SidebarItem[];

    constructor(props: IMappingBuilder) {
        super(props);

        this.tabs = this.getDefaultTabs();
        this.editContext = new MappingEditContext(props.definition);

        if (props.mappingBuilderName) {
            this.editContext.mappingBuilderName = props.mappingBuilderName;
        }

        if (props.onSave) {
            this.editContext.onSave = props.onSave;
        }

        if (props.onCancel) {
            this.editContext.onCancel = props.onCancel;
        }

        this.editContext.delayPosition();
    }

    getDefaultTabs(): SidebarItem[] {
        return [
            {
                value: MappingBuilderPage.Designer,
                renderUI: () => <>{this.RenderMappingBuilder()}</>,
                icon: <MappingIcon />,
            },
            {
                value: MappingBuilderPage.InputData,
                renderUI: () => (
                    <DataBuilder
                        definition={this.props.definition.getInputDefinition()}
                        readOnly={!this.props.definition.canChangeInputs}
                    />
                ),
                icon: <DataInputIcon />,
            },
            {
                value: MappingBuilderPage.OutputData,
                renderUI: () => (
                    <DataBuilder
                        definition={this.props.definition.getExecutionResult()}
                        readOnly={!this.props.definition.canChangeOutputs}
                    />
                ),
                icon: <DataOutputIcon />,
            },
            {
                value: MappingBuilderPage.Test,
                renderUI: () => (
                    <XExecutableTester
                        saveUnitTest={this.props.saveUnitTest}
                        executable={this.props.definition}
                    />
                ),
                icon: <TestDataIcon />,
            },
        ];
    }

    @AutoBind
    onChangedName(newName: string) {
        this.editContext.mappingBuilderName = newName;
        StateManager.propertyChanged(this.editContext, "title");
        if (this.props.onMappingBuilderNameUpdate) this.props.onMappingBuilderNameUpdate(newName);
    }

    RenderMappingBuilder() {
        const { definition } = this.props;
        return (
            <DndProvider backend={HTML5Backend}>
                <XMappingEditorRender>
                    <Xwrapper>
                        <XMappingSource
                            dataDefinition={definition.getInputDefinition()}
                            editContext={this.editContext}
                        />
                        <XMappingRules
                            definition={definition}
                            editContext={this.editContext}
                        />
                        <XMappingDestination
                            editContext={this.editContext}
                            definition={definition}
                        />
                        <ReactRenderHook />
                        <ObserveMultiState
                            listenTo={[definition, this.editContext, this.editContext.lineRefleshObject]}
                            control={() => (
                                <>
                                    {definition.mappingRules.map((mappingRule) => {
                                        let paths = mappingRule.getInboundPaths();

                                        let isCollapsed = this.editContext.isDestinationCollapsed(
                                            mappingRule.targetPath,
                                        );

                                        let highlighted = this.editContext.isHighlighted(mappingRule.id);

                                        // if (paths.length === 1) return null;
                                        return (
                                            <React.Fragment key={mappingRule.id}>
                                                {paths.map((p) => {
                                                    let source = this.editContext.sourceDragId(p);
                                                    let dest = isCollapsed
                                                        ? `${MappingConstants.MAPPING_PREFIX_RULE_ITEM}${mappingRule.targetPath}_connector_formappingrule`
                                                        : this.editContext.ruleIdForSource(mappingRule.id);
                                                    return (
                                                        <Xarrow
                                                            start={source}
                                                            end={dest}
                                                            startAnchor="right"
                                                            color={
                                                                highlighted
                                                                    ? MappingConstants.HIGHLIGHT_MAPPING_LINE_COLOR
                                                                    : MappingConstants.MAPPING_LINE_COLOR
                                                            }
                                                            strokeWidth={highlighted ? 2 : 1}
                                                            showHead={false}
                                                            key={p}
                                                        />
                                                    );
                                                })}
                                            </React.Fragment>
                                        );
                                    })}
                                </>
                            )}
                        />
                    </Xwrapper>
                </XMappingEditorRender>
            </DndProvider>
        );
    }

    render() {
        return (
            <BuilderRender>
                <ObserveState
                    listenTo={this.editContext}
                    properties={["title"]}
                    control={() => {
                        return (
                            <Topbar
                                onChangeName={this.onChangedName}
                                editContext={this.editContext}
                                name={this.editContext.mappingBuilderName}
                                isActionDisabled={true}
                                isRequiredActionGroup={false}
                                onSave={
                                    this.editContext.onSave
                                        ? () => {
                                              if (this.editContext.onSave) {
                                                  this.editContext.onSave(this.editContext.definition);
                                              }
                                          }
                                        : undefined
                                }
                                onClose={this.editContext.onCancel}
                            />
                        );
                    }}
                />
                <ObserveState
                    listenTo={this.editContext}
                    properties={["mode"]}
                    control={() => (
                        <Sidebar
                            items={this.tabs}
                            value={this.editContext.mode}
                            onChange={(mode: string) => {
                                Reflect.set(this.editContext, "mode", mode);
                                StateManager.propertyChanged(this.editContext, "mode");
                            }}
                        />
                    )}
                />
            </BuilderRender>
        );
    }
}

const XMappingEditorRender = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: row;
    background: ${UIStyleConstants.WHITE_COLOR};
`;

export const BuilderRender = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
`;
